import { SyntheticEvent, useEffect } from "react";
import { useEditorStore } from "@/lib/stores/editor-store/editor-store";
import { isActiveElementInput } from "@/lib/utils";
import { VIDEO_PREVIEW_CONTAINER_ID } from "./VideoPreview";

/**
 * Global keyboard shortcut: toggle the editor player play/pause when the user
 * presses the space bar.
 *
 * To avoid hijacking Space from other UI (typing in inputs, activating focused
 * buttons, etc.), the shortcut only fires when focus is on `document.body`
 * (nothing focused) or inside the video preview container itself.
 */
export function useSpaceToTogglePlay() {
  const playerRef = useEditorStore((state) => state.service.getPlayerRef());

  useEffect(() => {
    if (!playerRef) {
      return;
    }

    const handleKeyDown = (e: KeyboardEvent) => {
      // Typing a space, e.g. while editing text on the canvas.
      if (e.code !== "Space" || isActiveElementInput()) {
        return;
      }

      const target = e.target as HTMLElement | null;
      const previewContainer = document.getElementById(
        VIDEO_PREVIEW_CONTAINER_ID,
      );

      const isAllowedTarget =
        target === document.body ||
        (!!previewContainer && !!target && previewContainer.contains(target));

      if (!isAllowedTarget) {
        return;
      }

      // Prevent page scroll and avoid double-toggling when focus is inside the
      // Remotion Player (which has its own space-to-play handler).
      e.preventDefault();
      playerRef.current?.toggle(e as unknown as SyntheticEvent);
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [playerRef]);
}
